import { STORAGE_KEYS, getStorageItem, setStorageItem, removeStorageItem } from '../constants/storage.constants';
import { TOKEN_CONFIG } from '../constants/api.constants';

/**
 * Get access token from localStorage
 * @returns {string|null} Access token
 */
export const getAccessToken = () => {
  return getStorageItem(STORAGE_KEYS.ACCESS_TOKEN);
};

/**
 * Get refresh token from localStorage
 * @returns {string|null} Refresh token
 */
export const getRefreshToken = () => {
  return getStorageItem(STORAGE_KEYS.REFRESH_TOKEN);
};

/**
 * Get token creation date
 * @returns {string|null} ISO date string
 */
export const getTokenCreationDate = () => {
  return getStorageItem(STORAGE_KEYS.TOKEN_CREATION_DATE);
};

/**
 * Set access token
 * @param {string} token - Access token
 * @returns {boolean} Success status
 */
export const setAccessToken = (token) => {
  return setStorageItem(STORAGE_KEYS.ACCESS_TOKEN, token);
};

/**
 * Set refresh token
 * @param {string} token - Refresh token
 * @returns {boolean} Success status
 */
export const setRefreshToken = (token) => {
  return setStorageItem(STORAGE_KEYS.REFRESH_TOKEN, token);
};

/**
 * Set token creation date
 * @param {string} date - ISO date string
 * @returns {boolean} Success status
 */
export const setTokenCreationDate = (date) => {
  return setStorageItem(STORAGE_KEYS.TOKEN_CREATION_DATE, date);
};

/**
 * Set all token data at once
 * @param {Object} tokenData - Token data object
 * @param {string} tokenData.accessToken - Access token
 * @param {string} tokenData.refreshToken - Refresh token
 * @param {string} tokenData.creationDate - Creation date
 * @returns {boolean} Success status
 */
export const setTokenData = ({ accessToken, refreshToken, creationDate }) => {
  let success = true;
  if (accessToken) success = success && setAccessToken(accessToken);
  if (refreshToken) success = success && setRefreshToken(refreshToken);
  if (creationDate) success = success && setTokenCreationDate(creationDate);
  return success;
};

/**
 * Clear all token data
 * @returns {boolean} Success status
 */
export const clearTokenData = () => {
  let success = true;
  success = success && removeStorageItem(STORAGE_KEYS.ACCESS_TOKEN);
  success = success && removeStorageItem(STORAGE_KEYS.REFRESH_TOKEN);
  success = success && removeStorageItem(STORAGE_KEYS.TOKEN_CREATION_DATE);
  return success;
};

/**
 * Calculate time until token expires
 * @param {string} creationDate - ISO date string
 * @returns {number} Milliseconds until expiry (negative if expired)
 */
export const getTimeUntilExpiry = (creationDate) => {
  if (!creationDate) return -1;
  
  const created = new Date(creationDate).getTime();
  const expiry = created + TOKEN_CONFIG.tokenLifetime;
  return expiry - Date.now();
};

/**
 * Check if token should be refreshed
 * @param {string} creationDate - ISO date string
 * @returns {boolean} True if token should be refreshed
 */
export const shouldRefreshToken = (creationDate) => {
  const timeUntilExpiry = getTimeUntilExpiry(creationDate);
  return timeUntilExpiry > 0 && timeUntilExpiry <= TOKEN_CONFIG.refreshThreshold;
};

/**
 * Check if token is expired
 * @param {string} creationDate - ISO date string
 * @returns {boolean} True if token is expired
 */
export const isTokenExpired = (creationDate) => {
  return getTimeUntilExpiry(creationDate) <= 0;
};

/**
 * Get token expiry date
 * @param {string} creationDate - ISO date string
 * @returns {Date|null} Expiry date
 */
export const getTokenExpiryDate = (creationDate) => {
  if (!creationDate) return null;
  
  const created = new Date(creationDate).getTime();
  return new Date(created + TOKEN_CONFIG.tokenLifetime);
};

/**
 * Format time until expiry as human-readable string
 * @param {string} creationDate - ISO date string
 * @returns {string} Formatted time (e.g., "15m 30s")
 */
export const formatTimeUntilExpiry = (creationDate) => {
  const timeMs = getTimeUntilExpiry(creationDate);
  if (timeMs <= 0) return 'Expired';
  
  const minutes = Math.floor(timeMs / 60000);
  const seconds = Math.floor((timeMs % 60000) / 1000);
  
  return `${minutes}m ${seconds}s`;
};

/**
 * Check if current token is valid
 * @returns {boolean} True if token exists and is valid
 */
export const isTokenValid = () => {
  const token = getAccessToken();
  const creationDate = getTokenCreationDate();
  
  if (!token || !creationDate) return false;
  
  return !isTokenExpired(creationDate);
};

/**
 * Get complete token state
 * @returns {Object} Token state object
 */
export const getTokenState = () => {
  const accessToken = getAccessToken();
  const refreshToken = getRefreshToken();
  const creationDate = getTokenCreationDate();
  
  return {
    accessToken,
    refreshToken,
    creationDate,
    isValid: isTokenValid(),
    shouldRefresh: creationDate ? shouldRefreshToken(creationDate) : false,
    timeUntilExpiry: creationDate ? getTimeUntilExpiry(creationDate) : -1,
    expiryDate: creationDate ? getTokenExpiryDate(creationDate) : null,
    formattedTimeRemaining: creationDate ? formatTimeUntilExpiry(creationDate) : 'No token',
  };
};

/**
 * Get authorization header
 * @returns {Object} Authorization header object
 */
export const getAuthHeader = () => {
  const token = getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Get authorization headers with additional headers
 * @param {Object} additionalHeaders - Additional headers to merge
 * @returns {Object} Complete headers object
 */
export const getAuthHeaders = (additionalHeaders = {}) => {
  return {
    ...getAuthHeader(),
    ...additionalHeaders,
  };
};
